import React, { useMemo, useRef, useState } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { Code2, Palette, Plus } from "lucide-react";
import SearchableSelect from "modules/swiftboard/components/ui/SearchableSelect";

const QUILL_MODULES = {
  toolbar: [
    [{ header: [false, 2, 3] }],
    ["bold", "italic", "underline"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    [{ align: [] }],
    ["clean"],
  ],
};

const placeholderLabel = (key) =>
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Global dual-mode rich HTML editor — a "Design" (Quill WYSIWYG) view and a
// raw "HTML" source view that both read/write the same `value` HTML string,
// so switching tabs never loses content. Reusable anywhere the app needs
// premium HTML authoring (not just email templates).
//
// Usage:
//   <RichHtmlEditor value={bodyHtml} onChange={setBodyHtml} placeholders={["first_name"]} />
const RichHtmlEditor = ({ value, onChange, placeholders = [], minHeight = 260 }) => {
  const [mode, setMode] = useState("design");
  const [customKey, setCustomKey] = useState("");
  const quillRef = useRef(null);

  const placeholderOptions = useMemo(
    () => placeholders.map((key) => ({ value: key, label: `${placeholderLabel(key)} — {{${key}}}` })),
    [placeholders]
  );

  const insertToken = (key) => {
    if (!key?.trim()) return;
    const token = `{{${key.trim()}}}`;
    if (mode === "design" && quillRef.current) {
      const editor = quillRef.current.getEditor();
      const range = editor.getSelection(true);
      const index = range ? range.index : editor.getLength();
      editor.insertText(index, token, "user");
      editor.setSelection(index + token.length, 0, "user");
    } else {
      onChange(`${value || ""}${token}`);
    }
  };

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-surface">
      {/* Tabs + placeholder tools */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-border bg-surface_alt">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setMode("design")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md ${
              mode === "design" ? "bg-primary text-white" : "bg-surface text-text_secondary border border-border"
            }`}
          >
            <Palette size={12} /> Design
          </button>
          <button
            type="button"
            onClick={() => setMode("html")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md ${
              mode === "html" ? "bg-primary text-white" : "bg-surface text-text_secondary border border-border"
            }`}
          >
            <Code2 size={12} /> HTML
          </button>
        </div>

        <div className="flex items-center gap-1.5">
          {placeholderOptions.length > 0 && (
            <div className="w-48">
              <SearchableSelect
                options={placeholderOptions}
                value=""
                onChange={(val) => val && insertToken(val)}
                placeholder="Insert field…"
              />
            </div>
          )}
          <input
            type="text"
            value={customKey}
            onChange={(e) => setCustomKey(e.target.value.replace(/[^a-zA-Z0-9_]/g, ""))}
            placeholder="custom_key"
            className="w-28 px-2 py-1.5 text-xs rounded-md border border-border bg-surface text-text_primary placeholder:text-text_muted focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          />
          <button
            type="button"
            onClick={() => { insertToken(customKey); setCustomKey(""); }}
            disabled={!customKey.trim()}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-md bg-surface text-text_secondary border border-border disabled:opacity-50"
          >
            <Plus size={12} /> Insert
          </button>
        </div>
      </div>

      {/* Editor */}
      {mode === "design" ? (
        <div className="rich-html-editor-quill" style={{ minHeight }}>
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={value || ""}
            onChange={onChange}
            modules={QUILL_MODULES}
            style={{ minHeight }}
          />
        </div>
      ) : (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={12}
          spellCheck={false}
          style={{ minHeight }}
          className="w-full px-3 py-2.5 text-xs font-mono rounded-none border-0 bg-surface text-text_primary placeholder:text-text_muted focus:outline-none resize-y"
          placeholder="<p>Hello {{first_name}},</p>"
        />
      )}

      <style>{`
        .rich-html-editor-quill .ql-toolbar.ql-snow {
          border: none;
          border-bottom: 1px solid var(--color-border);
          background: var(--color-surface-alt);
        }
        .rich-html-editor-quill .ql-container.ql-snow {
          border: none;
          font-size: 13px;
          background: var(--color-surface);
          color: var(--color-text-primary);
        }
        .rich-html-editor-quill .ql-editor {
          min-height: ${minHeight - 42}px;
          color: var(--color-text-primary); 
          white-space: normal !important;
          overflow-wrap: break-word !important;
          word-break: normal !important;
          hyphens: none !important;
        }
        .rich-html-editor-quill .ql-snow .ql-stroke { stroke: var(--color-text-secondary); }
        .rich-html-editor-quill .ql-snow .ql-fill { fill: var(--color-text-secondary); }
        .rich-html-editor-quill .ql-snow .ql-picker { color: var(--color-text-secondary); }
        .rich-html-editor-quill .ql-editor.ql-blank::before {
          color: var(--color-text-muted);
          font-style: normal;
        }
      `}</style>
    </div>
  );
};

export default RichHtmlEditor;
